import { EmptyPlaceholderIcon } from '@app/assets/icons/vcard';
import { Avatar, Button, Typography } from '@app/components';
import { NO_LOGO_ID } from '@app/components/gallery-editor/components/image-item/ImageItem';
import { useGetLibraryById } from '@app/domain/library/api/library.hooks.api';
import { WIFIFormSchema } from '@app/domain/qr-code/api/qr-code.form.schema.api';
import { clsxm } from '@app/styles/clsxm';
import { WiFiQRCodeOutDto } from '@app/swagger-types';
import { useTranslation } from 'react-i18next';
import { QRCodePreviewWrap } from '../../QRCodePreviewWrap';
import { CopyIcon, LockIcon, WIFIIcon } from '@app/assets';
import { useClipboard } from '@app/hooks/useClipboard.hook';
import { useBackgroundImageDownload } from '@app/hooks/useBackgroundImageDownload.hook';
import { SvgScopeWrap } from '@app/utils/SvgScopeWrap';

const DEFAULT_WIFI_FORM_VALUES = {
  designBgColor: '#F3F4F6',
  designPrimaryColor: '#132A4D',
  designDescriptionColor: '#6B7280',
  designSecondaryColor: '#F3F4F6',
  designNameColor: '#FFFFFF',
  designTextColor: '#132a4d',
  designIconColor: '#455773',
  designTextHoverColor: '#132a4d',
  designTitleColor: '#6b7280',

  // TODO: Maby extend form fields
  //   buttonText: 'Add Contact',
  //   buttonSize: 14,
  //   buttonBgColor: '#FFFFFF',
  //   buttonTextColor: '#000000',
  //   buttonHoverColor: '#48B240',
  //   buttonBorderColor: '#E5E7EB',
};

export const WIFIPreview: React.FC<{
  wifi?: WiFiQRCodeOutDto | null;
  wifiFromForm?: WIFIFormSchema | null;
  isRenderFromForm?: boolean;
}> = ({ wifi, wifiFromForm, isRenderFromForm }) => {
  const { t } = useTranslation();
  const { copyToClipboard: copyPasswordToClipboard, isVisible } = useClipboard({});

  const currentWiFi = isRenderFromForm ? wifiFromForm : wifi;

  const { data: library } = useGetLibraryById(
    wifiFromForm?.libraryId !== NO_LOGO_ID ? wifiFromForm?.libraryId : undefined,
    {
      enabled: Boolean(isRenderFromForm),
    }
  );

  const fileId = isRenderFromForm ? library?.file?.id : wifi?.library?.file?.id;
  const [imageUrl, loading] = useBackgroundImageDownload(fileId, true);

  const mockDescription = !currentWiFi?.title && t('qr.multilinkInput.descriptionPlaceholder');
  const visibleDescription = currentWiFi?.description || mockDescription;

  return (
    <QRCodePreviewWrap
      isRenderFromForm={isRenderFromForm}
      style={{
        backgroundColor: DEFAULT_WIFI_FORM_VALUES.designBgColor,
        justifyContent: 'center',
      }}
      className="justify-center"
    >
      <div className="flex items-center justify-center">
        {!imageUrl && !loading && <WIFIIcon className="text-gray-300 mt-[50px] mb-6 h-[70px] w-[70px]" />}
        {(!imageUrl && loading) ||
          (imageUrl && (
            <div
              style={{
                backgroundColor: DEFAULT_WIFI_FORM_VALUES.designBgColor,
              }}
              className={clsxm('mt-[50px] mb-6 h-[70px] w-[70px] overflow-hidden rounded-[10px]')}
            >
              <Avatar
                isPublic
                imageId={fileId}
                className="rounded-none"
                skeletonVariant="rectangular"
                height="100%"
                width="100%"
              >
                <EmptyPlaceholderIcon />
              </Avatar>
            </div>
          ))}
      </div>
      <div className="flex flex-col gap-2">
        <Typography
          variant="l"
          className="block text-center font-bold text-white"
          style={{ wordBreak: 'break-word', color: DEFAULT_WIFI_FORM_VALUES?.designTitleColor }}
        >
          {currentWiFi?.title || t('qr.multilinkInput.title')}
        </Typography>
        {visibleDescription && (
          <Typography
            variant="xs"
            className="block text-center text-white"
            style={{ wordBreak: 'break-word', color: DEFAULT_WIFI_FORM_VALUES?.designDescriptionColor }}
          >
            {visibleDescription}
          </Typography>
        )}
      </div>
      <div className="flex flex-col gap-4 px-4 pt-8 mb-5">
        <div className="border-accept-dark-blue border bg-accept-dark-blue p-2 rounded-xl text-white text-center break-all font-bold">
          WIFI: {currentWiFi?.ssid || '-'}
        </div>
        <Button
          fullWidth
          size="medium"
          className="border-accept-dark-blue text-accept-dark-blue disabled:opacity-50 bg-transparent break-all"
          variant="outlined"
          disabled={!isRenderFromForm && (!currentWiFi?.password || currentWiFi?.encryption === 'NONE')}
          onClick={() => copyPasswordToClipboard(currentWiFi?.password || '')}
          endIcon={<SvgScopeWrap>{currentWiFi?.password ? <CopyIcon /> : <LockIcon />}</SvgScopeWrap>}
        >
          {isVisible && (currentWiFi?.password || '-')}
          {!isVisible && (
            <>
              {(currentWiFi?.password && currentWiFi?.encryption !== 'NONE') || isRenderFromForm
                ? t('qr.wifiInput.passwordLabel')
                : t('qr.wifiInput.noPassword')}
            </>
          )}
        </Button>
      </div>
      {currentWiFi?.encryption !== 'NONE' && (
        <div className="flex justify-center gap-2 items-center mb-4">
          <div className="bg-gray-200 rounded-lg p-1">
            <LockIcon className="text-accept-dark-blue" />
          </div>
          <span>{currentWiFi?.encryption}</span>
        </div>
      )}
    </QRCodePreviewWrap>
  );
};
